// ============================================================================
// src/lib/pastureMapReads.ts  —  the pasture SECDEF READ wrappers
// ----------------------------------------------------------------------------
// Split out of pastureMapApi so a route data resource can own the Pasture Map's
// reads. The split is not cosmetic and it is not optional:
//
// pastureMapApi binds the live browser client at module scope
// (`import {sb} from './supabase.js'`), and src/lib/supabase.ts reads
// `window.localStorage` while it initializes. src/app/data/routeDataCatalog is
// loaded outside a browser — by every static guard that joins the surface
// manifest to the catalog — so importing pastureMapApi from the catalog makes
// the whole manifest unloadable, with `window is not defined` thrown from a
// transitive import three modules away.
//
// So these seven follow the convention supabaseTypes.ts describes for nearly
// every other module here: the client is the FIRST PARAMETER and is never bound
// at module scope, which is also what lets a test inject a fake and what lets
// Query's AbortSignal reach the request. WRITES STAY IN pastureMapApi on its own
// client: a route resource is a render read, and keeping the two halves in two
// modules means an injected client can never be handed a mutation.
//
// Everything else is unchanged. These are the same wrappers over the same mig
// 116/127/128/129/132/140/147/150/180/202 RPCs, returning the same jsonb payload
// as `unknown` and throwing the same decorated error. All pasture tables are
// deny-all RLS; these RPCs remain the only client path to them.
// ============================================================================
import type {SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';

// The `{data, error}` pair a Supabase RPC settles with, narrowed to what
// unwrapPastureRpc reads. `data` stays unknown: the payloads are assembled
// server-side by SECDEF helpers whose column lists live in the pasture migration
// chain and are re-shaped by later migrations in it, so a transcribed row type
// would pin a shape that is already wrong while still passing a guard.
export type PastureRpcResult = {data: unknown; error: SupabaseErrorLike | null};

// unwrapPastureRpc decorates the thrown Error with the raw Supabase error and
// the RPC name, so callers can report which call failed. `cause` is ES2022's own
// Error field; `context` is this module's addition.
export type PastureMapError = Error & {context?: string};

/**
 * The single owner of the pasture RPC envelope contract, shared with the write
 * wrappers in pastureMapApi (which import it AS `unwrap`, so their call text —
 * and therefore their entries in the frozen render-read-site inventory — is
 * unchanged). The `PM_VALIDATION:` prefix strip is what turns a server-side gate
 * refusal into copy a field user can read.
 */
export function unwrapPastureRpc({data, error}: PastureRpcResult, label: string): unknown {
  if (error) {
    const msg = (error.message || String(error)).replace(/^PM_VALIDATION:\s*/, '');
    const e: PastureMapError = new Error(msg);
    e.cause = error;
    e.context = label;
    throw e;
  }
  return data;
}

// `abortSignal` is OPTIONAL on the constraint and re-checked at runtime, for the
// same reason routeDataCatalog's own `withSignal` does both: the stub clients in
// the catalog's tests are plain thenables that need not implement the builder
// method, and a missing signal must never become a TypeError inside a read.
// `then` is a REQUIRED member so the constraint is not a weak type.
type MaybeAbortableRequest<Request> = {then: unknown; abortSignal?: (signal: AbortSignal) => Request};

const withPastureReadSignal = <Request extends MaybeAbortableRequest<Request>>(
  request: Request,
  signal?: AbortSignal,
): Request => (signal && request && typeof request.abortSignal === 'function' ? request.abortSignal(signal) : request);

// List every live land area (geometry served as GeoJSON). Returns { land_areas:[...] }.
export async function listLandAreas(
  sb: SupabaseClient,
  includeDeleted = false,
  signal?: AbortSignal,
): Promise<unknown> {
  return unwrapPastureRpc(
    await withPastureReadSignal(sb.rpc('list_land_areas', {p_include_deleted: includeDeleted}), signal),
    'list_land_areas',
  );
}

export async function listPastureMoves(sb: SupabaseClient, limit = 100, signal?: AbortSignal): Promise<unknown> {
  return unwrapPastureRpc(
    await withPastureReadSignal(sb.rpc('list_pasture_moves', {p_limit: limit}), signal),
    'list_pasture_moves',
  );
}

export type PastureHistoryReportFilters = {
  landAreaId?: string | null;
  animalType?: string | null;
  groupKey?: string | null;
  limit?: number;
};

export async function listPastureHistoryReport(
  sb: SupabaseClient,
  {landAreaId = null, animalType = null, groupKey = null, limit = 200}: PastureHistoryReportFilters = {},
  signal?: AbortSignal,
): Promise<unknown> {
  return unwrapPastureRpc(
    await withPastureReadSignal(
      sb.rpc('list_pasture_history_report', {
        p_land_area_id: landAreaId,
        p_animal_type: animalType,
        p_group_key: groupKey,
        p_limit: limit,
      }),
      signal,
    ),
    'list_pasture_history_report',
  );
}

export async function listPastureRestReport(sb: SupabaseClient, signal?: AbortSignal): Promise<unknown> {
  return unwrapPastureRpc(
    await withPastureReadSignal(sb.rpc('list_pasture_rest_report'), signal),
    'list_pasture_rest_report',
  );
}

export type PastureStockingReportWindow = {since?: string | null; until?: string | null};

export async function listPastureStockingReport(
  sb: SupabaseClient,
  {since = null, until = null}: PastureStockingReportWindow = {},
  signal?: AbortSignal,
): Promise<unknown> {
  return unwrapPastureRpc(
    await withPastureReadSignal(sb.rpc('list_pasture_stocking_report', {p_since: since, p_until: until}), signal),
    'list_pasture_stocking_report',
  );
}

// CP-C (V1 reset) — shared, persisted MANUAL rotations (mig 140). Read + edit are
// farm_team-level incl. light. area_ids is the user's ordered path; archived /
// deleted destination ids are filtered out client-side on render.
export async function listPastureRotations(sb: SupabaseClient, signal?: AbortSignal): Promise<unknown> {
  return unwrapPastureRpc(
    await withPastureReadSignal(sb.rpc('list_pasture_rotations'), signal),
    'list_pasture_rotations',
  );
}

export async function listPastureMeasurements(sb: SupabaseClient, signal?: AbortSignal): Promise<unknown> {
  return unwrapPastureRpc(
    await withPastureReadSignal(sb.rpc('list_pasture_measurements'), signal),
    'list_pasture_measurements',
  );
}
